import { ValueOf } from "core/types";
import { Banner } from "ds/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";

export const HEADER_BANNERS = {
  EXTRACT_COMPLETED: "extract-completed",
  EXTRACT_COMPLETED_NONE_FILLED: "extract-completed-none-filled",
  EXTRACT_FAILED: "extract-failed",
  EXTRACTING: "extracting",
  FORM_DIRTY_WARNING: "form-dirty-warning",
  IMPORT_WARNING: "import-warning",
  NO_FILES_WARNING: "no-files-warning",
} as const;

export type HeaderBannerType = ValueOf<typeof HEADER_BANNERS>;

export type HeaderBannerAction =
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_COMPLETED;
      context: { numFilledFields: number };
    }
  | {
      banner: typeof HEADER_BANNERS.IMPORT_WARNING;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACTING;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_FAILED;
    }
  | {
      banner: typeof HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED;
    }
  | {
      banner: typeof HEADER_BANNERS.NO_FILES_WARNING;
    }
  | {
      banner: typeof HEADER_BANNERS.FORM_DIRTY_WARNING;
    }
  | {
      banner: null;
    };

export const HeaderBanners = ({
  headerBannerAction,
  importData,
  onClose,
}: {
  headerBannerAction: HeaderBannerAction;
  importData: () => void;
  onClose: () => void;
}) => {
  const translations = useTranslations();
  const [showExtracting, setShowExtracting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setFormModal } = useRecareDocsContext();

  // delay showing the extracting banner for 1 second
  useEffect(() => {
    if (headerBannerAction.banner === HEADER_BANNERS.EXTRACTING) {
      timeoutRef.current = setTimeout(() => {
        setShowExtracting(true);
      }, 1000);
    } else if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [headerBannerAction.banner]);

  switch (headerBannerAction.banner) {
    case HEADER_BANNERS.IMPORT_WARNING:
      return (
        <Banner
          actions={[
            {
              id: "refresh",
              label:
                translations.extractDocs.docsCreation.refreshWarningModal
                  .buttonRefresh,
              onPress: importData,
            },
          ]}
          color="warning"
          heading={
            translations.extractDocs.docsCreation.refreshWarningModal.title
          }
          message={
            translations.extractDocs.docsCreation.refreshWarningModal
              .description
          }
          withClose
          onClose={onClose}
        />
      );
    case HEADER_BANNERS.EXTRACT_COMPLETED:
      return (
        <Banner
          color="success"
          heading={translations.extractDocs.docsCreation.importModalSummary.title(
            {
              count: headerBannerAction.context.numFilledFields.toString(),
            },
          )}
          message={
            <div className="flex flex-col gap-4">
              <p>
                {
                  translations.extractDocs.docsCreation.importModalSummary
                    .filledWithExtractedData
                }
              </p>
              <p className="font-bold">
                {
                  translations.extractDocs.docsCreation.importModalSummary
                    .disclaimerExtractedData
                }
              </p>
            </div>
          }
          withClose
        />
      );
    case HEADER_BANNERS.EXTRACTING:
      {
        if (showExtracting)
          return (
            <Banner
              color="primary"
              heading={
                translations.extractDocs.docsCreation.importModalLoading.title
              }
              isLoading
              message={
                translations.extractDocs.docsCreation.importModalLoading
                  .description
              }
            />
          );
      }
      break;
    case HEADER_BANNERS.EXTRACT_FAILED:
      return (
        <Banner
          color="danger"
          message="translation: extraction failed"
          withClose
        />
      );
    case HEADER_BANNERS.NO_FILES_WARNING:
      return (
        <Banner
          color="warning"
          heading={
            translations.extractDocs.docsCreation.bannerNoDocumentsAvailable
              .title
          }
          message={
            translations.extractDocs.docsCreation.bannerNoDocumentsAvailable
              .description
          }
          withClose
        />
      );
    case HEADER_BANNERS.FORM_DIRTY_WARNING:
      return (
        <Banner
          actions={[
            {
              id: "close",
              label:
                translations.extractDocs.splitView.header.buttonConfirmClose,
              onPress: () => setFormModal(null),
            },
            {
              id: "cancel",
              label: translations.extractDocs.splitView.header.buttonCancel,
              onPress: onClose,
            },
          ]}
          color="warning"
          heading={
            translations.extractDocs.splitView.header.bannerCloseWarningTitle
          }
          message={
            translations.extractDocs.splitView.header
              .bannerCloseWarningDescription
          }
        />
      );
    case HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED:
      return (
        <Banner
          color="warning"
          heading={
            translations.extractDocs.docsCreation.bannerNoExtractedDataImported
              .title
          }
          message={
            translations.extractDocs.docsCreation.bannerNoExtractedDataImported
              .description
          }
          withClose
        />
      );
    default:
      return null;
  }
};
